import { useState, useEffect } from "react";
import { Plus, Trash2, Rows3, Settings2, BadgeCheck, Save, ImagePlus, X, Loader2, ChevronUp, ChevronDown, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const input = "bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet transition-colors text-sm";
const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Unidades de medida frecuentes → clave de unidad SAT (obligatoria en el CFDI).
export const SAT_UNITS = [
  { label: "Pieza", key: "H87" },
  { label: "Servicio", key: "E48" },
  { label: "Set / Juego", key: "SET" },
  { label: "Kit", key: "KT" },
  { label: "Actividad", key: "ACT" },
  { label: "Metro", key: "MTR" },
  { label: "Metro cuadrado", key: "MTK" },
  { label: "Metro lineal", key: "LM" },
  { label: "Hora", key: "HUR" },
  { label: "Día", key: "DAY" },
  { label: "Caja", key: "XBX" },
  { label: "Paquete", key: "XPK" },
  { label: "Litro", key: "LTR" },
  { label: "Kilogramo", key: "KGM" },
];

// Calcula los totales replicando exactamente la lógica del backend (studio.compute_quote).
export function computeQuote(v) {
  const items = v.items || [];
  const marginPct = Number(v.margin_pct || 0);
  const taxPct = Number(v.tax_pct || 0);
  let subtotal = 0, gross = 0;
  const sections = [];
  let cur = null;
  items.forEach((it) => {
    if (it.kind === "header") { cur = { label: it.description, subtotal: 0 }; sections.push(cur); return; }
    const line = Number(it.qty || 0) * Number(it.unit_price || 0);
    const net = line * (1 - Number(it.discount_pct || 0) / 100);
    gross += line; subtotal += net;
    if (cur) cur.subtotal += net;
  });
  const discount = gross - subtotal;
  const margin = v.margin_included ? 0 : (subtotal * marginPct) / 100;
  const baseRaw = subtotal + margin;
  const credit = Math.min(Math.max(Number(v.credit_amount || 0), 0), baseRaw);
  const base = baseRaw - credit;
  const tax = v.tax_included ? (taxPct ? base - base / (1 + taxPct / 100) : 0) : (base * taxPct) / 100;
  const total = v.tax_included ? base : base + tax;
  return { subtotal, discount, credit, margin, tax, total, sections };
}

// Editor de presupuesto reutilizable (CRM lead + Studio). Controlado por `value`/`onChange`.
export default function QuoteBuilder({ value, onChange, readOnly = false }) {
  const v = { items: [], margin_pct: 0, tax_pct: 16, margin_included: false, tax_included: false, hide_total: false, ...value };
  const [openFiscal, setOpenFiscal] = useState({});
  const [catalog, setCatalog] = useState([]);
  const [uploading, setUploading] = useState({});
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  useEffect(() => {
    api.get("/facturacion/catalog/products").then((r) => setCatalog(r.data || [])).catch(() => setCatalog([]));
  }, []);

  const set = (patch) => onChange({ ...v, ...patch });
  const setItem = (i, patch) => set({ items: v.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
  const addItem = () => set({ items: [...v.items, { id: String(Date.now()), kind: "item", description: "", qty: 1, unit_price: 0, discount_pct: 0, product_key: "", sku: "", unit: "", unit_key: "" }] });
  const addHeader = () => set({ items: [...v.items, { id: String(Date.now()), kind: "header", description: "", discount_pct: 0 }] });
  const rm = (i) => set({ items: v.items.filter((_, j) => j !== i) });
  // Reordenar e insertar en CUALQUIER posición del presupuesto (no solo al final).
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= v.items.length) return;
    const items = [...v.items];
    [items[i], items[j]] = [items[j], items[i]];
    set({ items });
  };
  const insertAt = (i, kind) => {
    const base = kind === "header"
      ? { id: String(Date.now()), kind: "header", description: "", discount_pct: 0 }
      : { id: String(Date.now()), kind: "item", description: "", qty: 1, unit_price: 0, discount_pct: 0, product_key: "", sku: "", unit: "", unit_key: "", images: [] };
    const items = [...v.items];
    items.splice(i + 1, 0, base);
    set({ items });
  };
  // Arrastrar y soltar para reordenar (mueve el elemento arrastrado a la posición destino).
  const reorder = (from, to) => {
    if (from == null || to == null || from === to) return;
    const items = [...v.items];
    const [m] = items.splice(from, 1);
    items.splice(to > from ? to - 1 : to, 0, m);
    set({ items });
  };
  const dragProps = (i) => readOnly ? {} : {
    onDragOver: (e) => { e.preventDefault(); if (overIdx !== i) setOverIdx(i); },
    onDrop: (e) => { e.preventDefault(); reorder(dragIdx, i); setDragIdx(null); setOverIdx(null); },
  };
  const toggleFiscal = (i) => setOpenFiscal((o) => ({ ...o, [i]: !o[i] }));
  // Descuento por sección: aplica el % a todas las partidas hasta el siguiente encabezado.
  const setSectionDiscount = (i, val) => {
    const items = v.items.map((it) => ({ ...it }));
    items[i].discount_pct = val;
    for (let j = i + 1; j < items.length && items[j].kind !== "header"; j++) items[j].discount_pct = val;
    set({ items });
  };
  const setUnit = (i, label) => {
    const u = SAT_UNITS.find((x) => x.label === label);
    setItem(i, { unit: label, unit_key: u ? u.key : "" });
  };
  const saveToCatalog = async (it) => {
    if (!it.product_key) { toast.error("Agrega la clave SAT antes de guardar en el catálogo"); return; }
    try {
      await api.post("/facturacion/catalog/products", {
        product_key: it.product_key, unit_key: it.unit_key || "E48", description: it.description || "",
      });
      const r = await api.get("/facturacion/catalog/products");
      setCatalog(r.data || []);
      toast.success("Concepto guardado en tu catálogo de productos");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar en el catálogo"); }
  };
  const c = computeQuote(v);

  // Subida de fotos por concepto o sección (directo desde la computadora).
  const uploadImages = async (i, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading((u) => ({ ...u, [i]: true }));
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("private", "false");
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data?.url) urls.push(data.url);
      }
      const it = v.items[i];
      setItem(i, { images: [...(it.images || []), ...urls] });
      toast.success(urls.length > 1 ? `${urls.length} fotos agregadas` : "Foto agregada");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo subir la foto"); }
    finally { setUploading((u) => ({ ...u, [i]: false })); }
  };
  const removeImage = (i, idx) => setItem(i, { images: (v.items[i].images || []).filter((_, k) => k !== idx) });

  // Barra de acciones por fila: mover arriba/abajo e insertar concepto/sección justo debajo.
  const RowTools = ({ i }) => readOnly ? null : (
    <div className="flex items-center gap-1 mb-1 opacity-0 group-hover/row:opacity-100 focus-within:opacity-100 transition-opacity" data-testid={`qb-tools-${i}`}>
      <span draggable onDragStart={() => setDragIdx(i)} onDragEnd={() => { setDragIdx(null); setOverIdx(null); }} className="p-1 rounded text-navy/40 hover:text-violet hover:bg-violet/10 cursor-grab active:cursor-grabbing" title="Arrastra para reordenar" data-testid={`qb-drag-${i}`}><GripVertical className="w-3.5 h-3.5" /></span>
      <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 rounded text-navy/40 hover:text-violet hover:bg-violet/10 disabled:opacity-25" title="Subir" data-testid={`qb-move-up-${i}`}><ChevronUp className="w-3.5 h-3.5" /></button>
      <button onClick={() => move(i, 1)} disabled={i === v.items.length - 1} className="p-1 rounded text-navy/40 hover:text-violet hover:bg-violet/10 disabled:opacity-25" title="Bajar" data-testid={`qb-move-down-${i}`}><ChevronDown className="w-3.5 h-3.5" /></button>
      <span className="w-px h-3.5 bg-navy/10 mx-0.5" />
      <button onClick={() => insertAt(i, "item")} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded text-violet hover:bg-violet/10" title="Insertar concepto debajo" data-testid={`qb-insert-item-${i}`}><Plus className="w-3 h-3" /> concepto aquí</button>
      <button onClick={() => insertAt(i, "header")} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded text-violet hover:bg-violet/10" title="Insertar sección debajo" data-testid={`qb-insert-header-${i}`}><Rows3 className="w-3 h-3" /> sección aquí</button>
    </div>
  );

  const ImageStrip = ({ i, it, label }) => (
    <div className="flex flex-wrap items-center gap-2 mt-1" data-testid={`qb-images-${i}`}>
      {(it.images || []).map((src, k) => (
        <div key={k} className="relative group/img">
          <img src={src} alt="" className="h-16 w-16 object-cover rounded-lg border border-navy/10" />
          {!readOnly && <button onClick={() => removeImage(i, k)} className="absolute -top-1.5 -right-1.5 bg-white border border-navy/10 rounded-full p-0.5 text-navy/50 hover:text-destructive shadow-sm" data-testid={`qb-img-del-${i}-${k}`}><X className="w-3 h-3" /></button>}
        </div>
      ))}
      {!readOnly && (
        <label className="h-16 w-16 rounded-lg border border-dashed border-violet/40 text-violet/70 hover:bg-violet/5 flex flex-col items-center justify-center cursor-pointer text-[10px] gap-0.5" data-testid={`qb-img-add-${i}`} title={label || "Agregar foto"}>
          {uploading[i] ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ImagePlus className="w-4 h-4" /><span>Foto</span></>}
          <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { uploadImages(i, e.target.files); e.target.value = ""; }} />
        </label>
      )}
    </div>
  );

  // Subtotal acumulado por sección para mostrarlo al cerrar cada bloque.
  const secSub = [];
  { let run = 0, open = false;
    v.items.forEach((it, i) => {
      if (it.kind === "header") { secSub[i] = null; run = 0; open = true; }
      else { run += Number(it.qty || 0) * Number(it.unit_price || 0) * (1 - Number(it.discount_pct || 0) / 100);
        const next = v.items[i + 1];
        secSub[i] = (open && (!next || next.kind === "header")) ? run : null; }
    }); }

  return (
    <fieldset disabled={readOnly} className={`space-y-2 border-0 p-0 m-0 min-w-0 ${readOnly ? "opacity-95" : ""}`} data-testid="quote-builder">
      <datalist id="qb-satkeys">
        {catalog.map((p, i) => <option key={i} value={p.product_key}>{p.description || p.product_key}</option>)}
      </datalist>
      {v.items.map((it, i) =>
        it.kind === "header" ? (
          <div key={it.id || i} className={`group/row rounded-lg ${overIdx === i && dragIdx !== null && dragIdx !== i ? "ring-2 ring-violet/50" : ""}`} {...dragProps(i)}>
            <RowTools i={i} />
            <div data-testid={`qb-row-${i}`} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-8 flex items-center gap-2 rounded-lg bg-violet/10 border border-violet/20 px-3">
                <Rows3 className="w-4 h-4 text-violet shrink-0" />
                <input placeholder="Nombre de la sección (ej. Sala, Comedor, ½ baño)" value={it.description}
                  onChange={(e) => setItem(i, { description: e.target.value })}
                  className="w-full bg-transparent py-2 text-sm font-semibold text-violet placeholder:text-violet/40 outline-none uppercase tracking-wide"
                  data-testid={`qb-header-${i}`} />
              </div>
              <div className="col-span-3 flex items-center gap-1">
                <input type="number" placeholder="% desc" value={it.discount_pct || ""} onChange={(e) => setSectionDiscount(i, e.target.value)} className={`${input} w-full`} data-testid={`qb-sec-disc-${i}`} title="Descuento para toda la sección" />
              </div>
              <button onClick={() => rm(i)} className="col-span-1 text-navy/30 hover:text-destructive flex justify-center" data-testid={`qb-del-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
            <ImageStrip i={i} it={it} label="Foto de la sección" />
          </div>
        ) : (
          <div key={it.id || i} className={`group/row rounded-lg ${overIdx === i && dragIdx !== null && dragIdx !== i ? "ring-2 ring-violet/50" : ""}`} {...dragProps(i)}>
            <RowTools i={i} />
            <div data-testid={`qb-row-${i}`} className="grid grid-cols-12 gap-2 items-center">
              <input placeholder="Concepto" value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} className={`${input} col-span-4`} data-testid={`qb-desc-${i}`} />
              <input type="number" placeholder="Cant." value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} className={`${input} col-span-2`} data-testid={`qb-qty-${i}`} />
              <input type="number" placeholder="Precio" value={it.unit_price} onChange={(e) => setItem(i, { unit_price: e.target.value })} className={`${input} col-span-2`} data-testid={`qb-price-${i}`} />
              <input type="number" placeholder="% desc" value={it.discount_pct || ""} onChange={(e) => setItem(i, { discount_pct: e.target.value })} className={`${input} col-span-2`} data-testid={`qb-disc-${i}`} title="Descuento de la partida" />
              <button onClick={() => toggleFiscal(i)} className={`col-span-1 flex justify-center ${openFiscal[i] || it.product_key || it.unit ? "text-violet" : "text-navy/30"} hover:text-violet`} data-testid={`qb-fiscal-toggle-${i}`} title="Datos fiscales (clave SAT, código interno, unidad)"><Settings2 className="w-4 h-4" /></button>
              <button onClick={() => rm(i)} className="col-span-1 text-navy/30 hover:text-destructive flex justify-center" data-testid={`qb-del-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
            {openFiscal[i] && (
              <div className="mt-1 mb-2 rounded-lg bg-violet/5 border border-violet/15 p-3 grid grid-cols-12 gap-2 items-end" data-testid={`qb-fiscal-${i}`}>
                <div className="col-span-4">
                  <label className="text-[11px] text-navy/50 block mb-0.5">Clave SAT</label>
                  <input list="qb-satkeys" placeholder="Ej. 56101500" value={it.product_key || ""} onChange={(e) => setItem(i, { product_key: e.target.value })} className={`${input} w-full`} data-testid={`qb-prodkey-${i}`} />
                </div>
                <div className="col-span-3">
                  <label className="text-[11px] text-navy/50 block mb-0.5">Código interno</label>
                  <input placeholder="SKU" value={it.sku || ""} onChange={(e) => setItem(i, { sku: e.target.value })} className={`${input} w-full`} data-testid={`qb-sku-${i}`} />
                </div>
                <div className="col-span-3">
                  <label className="text-[11px] text-navy/50 block mb-0.5">Unidad</label>
                  <select value={it.unit || ""} onChange={(e) => setUnit(i, e.target.value)} className={`${input} w-full`} data-testid={`qb-unit-${i}`}>
                    <option value="">— Unidad —</option>
                    {SAT_UNITS.map((u) => <option key={u.key} value={u.label}>{u.label}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <button onClick={() => saveToCatalog(it)} className="w-full inline-flex items-center justify-center gap-1 text-xs text-violet border border-violet/30 rounded-lg px-2 py-2 hover:bg-violet/10" data-testid={`qb-catalog-save-${i}`} title="Guardar este concepto en mi catálogo de productos"><Save className="w-3.5 h-3.5" /> Catálogo</button>
                </div>
              </div>
            )}
            <div className="pl-1"><ImageStrip i={i} it={it} label="Foto del concepto" /></div>
            {(it.invoiced || secSub[i] != null) && (
              <div className="flex items-center justify-end gap-3 pr-10 pt-1">
                {it.invoiced && <span className="inline-flex items-center gap-1 text-xs text-emerald-600" data-testid={`qb-invoiced-${i}`}><BadgeCheck className="w-3.5 h-3.5" /> Facturada</span>}
                {secSub[i] != null && <div className="text-right text-xs text-violet/70" data-testid={`qb-secsub-${i}`}>Subtotal sección: <b>{money(secSub[i])}</b></div>}
              </div>
            )}
          </div>
        )
      )}

      <div className="flex flex-wrap gap-4">
        <button data-testid="qb-add-item" onClick={addItem} className="text-sm text-violet hover:underline flex items-center gap-1"><Plus className="w-4 h-4" /> Añadir concepto</button>
        <button data-testid="qb-add-header" onClick={addHeader} className="text-sm text-violet hover:underline flex items-center gap-1"><Rows3 className="w-4 h-4" /> Añadir encabezado / sección</button>
      </div>

      <div className="pt-3 border-t border-navy/10 space-y-3">
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <label className="flex items-center gap-2 text-xs text-navy/70 cursor-pointer" data-testid="qb-margin-included">
            <input type="checkbox" checked={v.margin_included} onChange={(e) => set({ margin_included: e.target.checked })} />
            Mis precios ya incluyen el margen (no calcular margen)
          </label>
          <label className="flex items-center gap-2 text-xs text-navy/70 cursor-pointer" data-testid="qb-tax-included">
            <input type="checkbox" checked={v.tax_included} onChange={(e) => set({ tax_included: e.target.checked })} />
            El IVA ya está incluido en los precios (solo desglosar)
          </label>
          <label className="flex items-center gap-2 text-xs text-navy/70 cursor-pointer" data-testid="qb-hide-total">
            <input type="checkbox" checked={v.hide_total} onChange={(e) => set({ hide_total: e.target.checked })} />
            Ocultar el total (mostrar solo conceptos y su precio)
          </label>
        </div>
        <div className="flex flex-wrap gap-4 items-end">
          {!v.margin_included && (
            <div><label className="text-xs text-navy/50 block">Margen %</label><input type="number" value={v.margin_pct} onChange={(e) => set({ margin_pct: e.target.value })} className={`${input} w-24`} data-testid="qb-margin" /></div>
          )}
          <div><label className="text-xs text-navy/50 block">IVA %</label><input type="number" value={v.tax_pct} onChange={(e) => set({ tax_pct: e.target.value })} className={`${input} w-24`} data-testid="qb-tax" /></div>
          <div className="ml-auto text-right space-y-0.5">
            {(() => {
              // Desglose en la MISMA escala de los precios capturados.
              // - IVA incluido: los precios ya traen IVA → Subtotal(bruto) − Desc (+Margen) = Total; el IVA va dentro (informativo).
              // - IVA no incluido: Subtotal(sin IVA) − Desc (+Margen) + IVA = Total.
              const dGross = c.subtotal + c.discount;   // subtotal antes de descuento
              return (
                <div className="text-xs text-navy/50" data-testid="qb-breakdown">
                  Subtotal {money(dGross)}
                  {c.discount ? ` · Desc. −${money(c.discount)}` : ""}
                  {!v.margin_included && c.margin ? ` · Margen ${money(c.margin)}` : ""}
                  {c.tax ? ` · IVA${v.tax_included ? " incl." : ""} (${Number(v.tax_pct || 0)}%) ${money(c.tax)}` : ""}
                </div>
              );
            })()}
            <div className="text-2xl font-medium" data-testid="qb-total">{money(c.total)}</div>
            {v.hide_total && <div className="text-[11px] text-amber-600" data-testid="qb-total-hidden">Oculto en PDF y enlace</div>}
          </div>
        </div>
      </div>
    </fieldset>
  );
}
